import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import { Popover, TableCell } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import DialogConfirmDeleteArtwork from 'commons/Dialog/DialogConfirmDeleteArtwork';
import Text from 'commons/Text';
import API from 'configs/API';
import Colors from 'configs/Colors';
import useDeleteArtwork from 'hooks/useDeleteArtwork';
import CertificateDialog from 'pages/ArtworkDetail/components/CertificateDialog';
import DialogTransferOwnerCertificate from 'pages/ManageCertificate/components/ManageCertificateTable/components/ManageCertificateTableBody/components/TableCertificateAction/DialogTransferOwnerCertificate';
import useStatusCanRequestCertificate from 'react-query/hooks/queries/useStatusCanRequestCertificate';
import Routes from 'utils/Route';
import { axiosPatch } from 'utils/apis/axios';
import { ToastTopHelper } from 'utils/utils';

import useStyles from './styles';

const TableArtworkCollectorAction = ({ artwork }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [anchorEl, setAnchorEl] = useState(null);
  const [isShowDialogDelete, setIsShowDialogDelete] = useState(false);
  const [isShowTransferOwner, setIsShowTransferOwner] = useState(false);
  const [isOpenCertificateDialog, setIsOpenCertificateDialog] = useState(false);
  const [autoOpenDownload, setAutoOpenDownload] = useState(false);

  const { mutate: deleteArtwork } = useDeleteArtwork();
  const { data: status } = useStatusCanRequestCertificate();

  const currentUserId = useSelector(
    (state) => state.auth?.user?.id ?? state.auth?.account?.user?.id,
  );

  // Synthesize artwork with editions so useOwnedEditions can filter correctly.
  // The collector list API may not return editions with owner_certificate; fall back
  // to building a single-edition array from artwork.certificate if needed.
  const artworkForDownload = useMemo(() => {
    if (!artwork) return artwork;
    const hasEditions = Array.isArray(artwork.editions) && artwork.editions.length > 0;
    if (hasEditions) return artwork;
    const cert = artwork.certificate;
    if (!cert || !currentUserId) return artwork;
    return {
      ...artwork,
      editions: [
        {
          id: cert.id,
          edition_number: cert.edition_number,
          id_certificate: cert.id,
          owner_certificate: { id: currentUserId },
          total_edition: artwork.total_edition,
        },
      ],
    };
  }, [artwork, currentUserId]);

  const updateArtworkOwnerMutation = useMutation({
    mutationFn: ({ artworkUuid, newOwnerId }) => {
      return axiosPatch(false, API.ARTWORK.ARTWORK_DETAIL(artworkUuid), {
        owner: newOwnerId,
        status: 'in_stock',
      });
    },
  });

  const open = Boolean(anchorEl);

  const handleCloseDialog = () => {
    setIsOpenCertificateDialog(false);
    setAutoOpenDownload(false);
  };

  const handleOpenPopover = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleClickRequestCertificate = () => {
    navigate(Routes.RequestCertificateId(artwork?.uuid));
  };

  const handleNavigateEditArtwork = () => {
    navigate(Routes.EditArtwork(artwork?.uuid));
  };

  const handleShowPopUpConfirmDelete = () => {
    setAnchorEl(null);
    setIsShowDialogDelete(true);
  };

  const handleCancelDelete = () => {
    setIsShowDialogDelete(false);
  };

  const handleDeleteArtwork = () => {
    deleteArtwork(artwork?.uuid, {
      onSuccess: () => {
        ToastTopHelper.success(
          t(
            artwork?.has_certificate ? 'text_move_artwork_to_store' : 'text_delete_artwork_success',
          ),
        );
      },
    });

    setIsShowDialogDelete(false);
  };

  const handleOpenTransferOwner = () => {
    setAnchorEl(null);
    setIsShowTransferOwner(true);
  };

  const handleCloseTransferOwner = () => {
    setIsShowTransferOwner(false);
  };

  const handleSubmitTransferOwner = async (selectedUser) => {
    if (!artwork?.has_certificate || !artwork?.certificate?.code) {
      const { success } = await updateArtworkOwnerMutation.mutateAsync({
        artworkUuid: artwork?.uuid,
        newOwnerId: selectedUser?.id,
      });

      if (success) {
        ToastTopHelper.success(t('artwork_owner_updated'));
        queryClient.invalidateQueries({
          queryKey: [API.ARTWORK.LIST_ARTWORK_EXCLUDING_SOLD_DONATED_GIFTED],
        });
        queryClient.invalidateQueries({
          queryKey: [API.ARTWORK.LIST_COLLECTOR_SOLD_DONATED],
        });
        handleCloseTransferOwner();
      } else {
        ToastTopHelper.error(t('update_failed'));
      }
      return;
    }
  };

  const canRequestCertificate =
    Array.isArray(status) && status?.find((item) => item?.value === artwork?.status?.value);

  const isCertificateDisabled = !canRequestCertificate || artwork?.has_certificate;

  if (artwork?.is_ownership_transfer_record) {
    return (
      <TableCell className={classes.tableCell}>
        <Text type="sm">—</Text>
      </TableCell>
    );
  }

  return (
    <TableCell className={classes.tableCell}>
      <MoreHorizOutlinedIcon
        onClick={handleOpenPopover}
        className={classes.iconOutLine}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        className={classes.popover}
      >
        <Text
          type="sm"
          clickAble
          className={clsx(classes.textItemPopover, {
            [classes.disable]: isCertificateDisabled,
          })}
          onClick={handleClickRequestCertificate}
        >
          {t('request_certificate')}
        </Text>

        <Text
          clickAble
          onClick={() => {
            setAutoOpenDownload(true);
            setIsOpenCertificateDialog(true);
            setAnchorEl(null);
          }}
          className={clsx(classes.textItemPopover, {
            [classes.disable]: !artwork?.certificate || !artwork?.is_owner,
          })}
        >
          <Text type="ml">{t('download_certificate')}</Text>
        </Text>

        <Text
          type="sm"
          clickAble
          className={classes.textItemPopover}
          onClick={handleNavigateEditArtwork}
        >
          {t('edit_artwork')}
        </Text>

        <Text
          type="sm"
          clickAble
          className={clsx(classes.textItemPopover)}
          onClick={handleOpenTransferOwner}
        >
          {t('tranfer_owner')}
        </Text>

        <Text
          type="sm"
          clickAble
          onClick={handleShowPopUpConfirmDelete}
          className={classes.textItemPopover}
          color={Colors.Error}
        >
          {t('delete_artwork')}
        </Text>
      </Popover>

      <DialogConfirmDeleteArtwork
        isShowDialog={isShowDialogDelete}
        onCancel={handleCancelDelete}
        onSubmit={handleDeleteArtwork}
        hasCertificate={artwork?.has_certificate}
      />

      <DialogTransferOwnerCertificate
        open={isShowTransferOwner}
        onClose={handleCloseTransferOwner}
        onSubmit={handleSubmitTransferOwner}
        certificate={artwork?.certificate}
      />

      <CertificateDialog
        open={isOpenCertificateDialog}
        onClose={handleCloseDialog}
        artwork={artworkForDownload}
        certificate={artwork?.certificate}
        autoOpenDownload={autoOpenDownload}
      />
    </TableCell>
  );
};

export default TableArtworkCollectorAction;
